/**
 * R58-L-15 — Viewer readouts panel (top-right corner).
 *
 * Three lines stacked vertically:
 *   1. Radius (`r m`)        — hover-only per OQ-18; hidden on viewer-exit.
 *   2. Zoom level (`X %`)    — always visible.
 *   3. Pixel coords (`u, v`) — only when `showDebugOverlay` is true.
 *
 * Positioning rule from `20-ux-flows.md` §3.1: low-contrast tonal background,
 * minimal footprint, doesn't cover image content (≤ 200 px wide).
 *
 * SB6-ANA-VIEW-03 — radius prop semantics tightened:
 *   - `radiusMeters === undefined` → the row is HIDDEN (no geometry
 *     on the image at all; matches R58-L-15 AC4).
 *   - `radiusMeters === null` → the row is RENDERED with value `—`
 *     (geometry exists but no value for this cursor position). This is
 *     the SB6-ANA-VIEW-03 mode — Christian wants the readout always
 *     visible, "Including LOCKED", and to show `—` rather than collapse.
 *   - `radiusMeters` (number) → renders `"<value> m"` formatted to two
 *     locale decimals (e.g. `"23.41 m"`).
 *
 * The pic2comp math is still pending Story 032; `useRadiusReadout`
 * supplies an image-centre stand-in for now. When 032 lands, the hook
 * body swaps to the real per-pixel transform; this component does not
 * change.
 */
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import { formatRadiusReadout } from "./useRadiusReadout.js";
import { TriangleAlertIcon } from "@aeriosiq/ui/icons";

/** PERF-GPU-01 — tooltip copy for the downscale warning (story-locked). */
export const DOWNSCALE_WARNING_TOOLTIP =
  "This machine cannot display the full image resolution — the image is " +
  "shown downscaled. See the hardware requirements for details.";

export interface ViewerReadoutsProps {
  /** Cursor pixel coords (image-space) or null when cursor is outside. */
  cursorPixel: { u: number; v: number } | null;
  /** Current zoom expressed as a % of fit-to-viewport (100 = fit). */
  zoomPercent: number;
  /**
   * Radius in metres at the cursor. `null` means "off-blade or no
   * geometry yet" (rendered as "N/A"). `undefined` means "geometry is
   * unavailable for this image, hide the entire radius row" (AC4).
   */
  radiusMeters: number | null | undefined;
  /** L-15 AC6/AC7 — dev pixel-coord readout toggle. */
  showDebugOverlay: boolean;
  /**
   * PERF-GPU-01 (decision N-7) — true when the current image's native
   * dimensions exceed the device max-texture, i.e. the browser is showing
   * a silently DOWNSCALED image. Renders the ⚠️ row with the hardware-
   * requirements tooltip. The readouts panel is an absolute overlay, so
   * the row appearing/disappearing per image never shifts the viewer
   * chrome or canvas layout (AC3).
   */
  downscaleWarning?: boolean;
}

function MonoValue({
  children,
  testId,
}: {
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <Box
      component="span"
      data-testid={testId}
      sx={{
        fontFamily: "monospace",
        fontVariantNumeric: "tabular-nums",
        fontWeight: 600,
        ml: 1,
      }}
    >
      {children}
    </Box>
  );
}

export function ViewerReadouts({
  cursorPixel,
  zoomPercent,
  radiusMeters,
  showDebugOverlay,
  downscaleWarning = false,
}: ViewerReadoutsProps): React.ReactElement {
  // SB6-ANA-VIEW-03 — radius row visibility:
  //   - `undefined` → no geometry on the image; hide the row (R58-L-15
  //     AC4 retained for backwards-compat with the existing unit test).
  //   - `null` → row visible with `—` (geometry exists, no value for
  //     this cursor — the post-SB6 default for legacy images while
  //     pic2comp is pending).
  //   - number → render the formatted value.
  // The earlier hover-only gate (hide on cursor-leave) was relaxed:
  // Christian (SB6 brief) wants the readout visible in all states; the
  // cursor-stop behaviour stays via the parent's throttle on
  // onCursorImagePixel.
  const showRadius = radiusMeters !== undefined;
  const radiusText =
    radiusMeters === undefined ? "" : formatRadiusReadout(radiusMeters);

  return (
    <Box
      data-testid="viewer-readouts"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      sx={{
        position: "absolute",
        top: 8,
        right: 8,
        // AC8 — small footprint, low-contrast tonal background. Keep the
        // panel narrow + ensure the underlying pixels stay discernible.
        maxWidth: 200,
        px: 1,
        py: 0.5,
        borderRadius: 1,
        // Tonal background — slightly higher elevation than the wrapper's
        // background.paper for separation, ~70 % opacity so the image
        // peeks through.
        bgcolor: (t) =>
          t.palette.mode === "dark"
            ? "rgba(28, 28, 30, 0.72)"
            : "rgba(255, 255, 255, 0.78)",
        backdropFilter: "blur(4px)",
        // Above the OSD canvas + Konva overlay (which sit at zIndex 1-2)
        // but below the right-click marquee (zIndex 5).
        zIndex: 4,
        pointerEvents: "none",
      }}
    >
      <Stack spacing={0.25}>
        {downscaleWarning ? (
          <Typography
            variant="caption"
            component="div"
            data-testid="readout-downscale-row"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              color: "warning.main",
              // The panel disables pointer events so the image stays
              // interactive beneath it; re-enable them on this row only so
              // the tooltip is hoverable.
              pointerEvents: "auto",
            }}
          >
            <Tooltip title={DOWNSCALE_WARNING_TOOLTIP} placement="left" arrow>
              <TriangleAlertIcon
                data-testid="readout-downscale-warning"
                aria-label="Image shown downscaled"
                sx={{ fontSize: 14 }}
              />
            </Tooltip>
            Downscaled
          </Typography>
        ) : null}
        {showRadius ? (
          <Typography
            variant="caption"
            component="div"
            data-testid="readout-radius-row"
          >
            Radius:
            <MonoValue testId="readout-radius-value">{radiusText}</MonoValue>
          </Typography>
        ) : null}
        <Typography
          variant="caption"
          component="div"
          data-testid="readout-zoom-row"
        >
          Zoom:
          <MonoValue testId="readout-zoom-value">{zoomPercent} %</MonoValue>
        </Typography>
        {showDebugOverlay ? (
          <Typography
            variant="caption"
            component="div"
            data-testid="readout-pixel-row"
          >
            Pixel:
            <MonoValue testId="readout-pixel-value">
              {cursorPixel === null
                ? "—"
                : `${String(cursorPixel.u)}, ${String(cursorPixel.v)}`}
            </MonoValue>
          </Typography>
        ) : null}
      </Stack>
    </Box>
  );
}
